// Remote support — the HELPER's input encoder (shared/REMOTE-SUPPORT.md §4/§6). Pure: the viewer feeds it
// already-normalised pointer positions and DOM key/wheel values, it decides what goes on the wire.
//
//  - pointer moves are coalesced to ≤ 60/s; the last position is never lost (a trailing flush), and a
//    button or wheel event flushes the pending move first so the click lands where the pointer is;
//  - key-downs and key-ups pair up: on blur (or release, or loss of control) every key and button still
//    held is lifted, so nothing stays stuck on the requester's machine;
//  - auto-repeat key-downs are sent (the requester types the repeat like a held key would);
//  - the release combo Ctrl+Alt+Shift+R is never sent: it stops key forwarding until `resumeKeys()` (the
//    viewer clicked again). Pointer input keeps flowing.
//  - when control is not granted nothing is sent but `want`.
import { isReleaseCombo, MAX_MOVES_PER_SECOND, MAX_TEXT_CHARS, wheelPixels, type HelperMessage } from './protocol';

export interface EncoderClock {
  now(): number;
  /** Runs `fn` after `ms`; returns a cancel. */
  later(fn: () => void, ms: number): () => void;
}

export const realClock: EncoderClock = {
  now: () => performance.now(),
  later: (fn, ms) => {
    const id = setTimeout(fn, ms);
    return () => clearTimeout(id);
  },
};

export class InputEncoder {
  private readonly minGapMs = 1000 / MAX_MOVES_PER_SECOND;
  private lastMoveAt = -Infinity;
  private pendingMove: { x: number; y: number } | null = null;
  private cancelFlush: (() => void) | null = null;
  private readonly heldKeys = new Map<string, string>();
  private readonly heldButtons = new Set<number>();
  private lastX = 0.5;
  private lastY = 0.5;
  private controlGranted = false;
  private keysReleased = false;

  constructor(
    private readonly send: (message: HelperMessage) => void,
    private readonly clock: EncoderClock = realClock,
  ) {}

  get granted(): boolean {
    return this.controlGranted;
  }

  /** Whether the release combo has paused key forwarding (the viewer shows it). */
  get keysPaused(): boolean {
    return this.keysReleased;
  }

  setGranted(granted: boolean): void {
    if (granted === this.controlGranted) return;
    if (!granted) this.liftAll();
    this.controlGranted = granted;
    if (!granted) this.dropPendingMove();
  }

  /** «Pedir control» — the only message that goes out without control. */
  askForControl(): void {
    this.send({ t: 'want' });
  }

  pointerMove(x: number, y: number): void {
    if (!this.controlGranted) return;
    this.lastX = x;
    this.lastY = y;
    const now = this.clock.now();
    if (now - this.lastMoveAt >= this.minGapMs) {
      this.dropPendingMove();
      this.lastMoveAt = now;
      this.send({ t: 'mm', x, y });
      return;
    }
    this.pendingMove = { x, y };
    if (!this.cancelFlush) {
      this.cancelFlush = this.clock.later(() => {
        this.cancelFlush = null;
        this.flushMove();
      }, Math.max(0, this.minGapMs - (now - this.lastMoveAt)));
    }
  }

  pointerDown(button: number, x: number, y: number): void {
    if (!this.controlGranted || (button !== 0 && button !== 1 && button !== 2)) return;
    this.flushMove();
    this.lastX = x;
    this.lastY = y;
    if (this.heldButtons.has(button)) return;
    this.heldButtons.add(button);
    this.send({ t: 'md', b: button, x, y });
  }

  pointerUp(button: number, x: number, y: number): void {
    if (!this.controlGranted || !this.heldButtons.has(button)) return;
    this.flushMove();
    this.lastX = x;
    this.lastY = y;
    this.heldButtons.delete(button);
    this.send({ t: 'mu', b: button, x, y });
  }

  wheel(deltaX: number, deltaY: number, deltaMode: number): void {
    if (!this.controlGranted) return;
    const { dx, dy } = wheelPixels(deltaX, deltaY, deltaMode);
    if (!dx && !dy) return;
    this.flushMove();
    this.send({ t: 'wh', dx, dy });
  }

  /** Returns 'release' when this was the helper's release combo (the caller shows "keys paused"). */
  keyDown(code: string, key: string, m: number): 'sent' | 'release' | 'dropped' {
    if (isReleaseCombo(code, m)) {
      this.liftKeys();
      this.keysReleased = true;
      return 'release';
    }
    if (!this.controlGranted || this.keysReleased || !code) return 'dropped';
    this.heldKeys.set(code, key);
    this.send({ t: 'kd', code, key, m });
    return 'sent';
  }

  keyUp(code: string, key: string, m: number): void {
    if (!this.heldKeys.has(code)) return;
    this.heldKeys.delete(code);
    this.send({ t: 'ku', code, key, m });
  }

  /** Text a soft keyboard / IME produced (§4 `txt`). */
  text(s: string): void {
    if (!this.controlGranted || this.keysReleased || !s) return;
    for (let i = 0; i < s.length; i += MAX_TEXT_CHARS) this.send({ t: 'txt', s: s.slice(i, i + MAX_TEXT_CHARS) });
  }

  /** The viewer was clicked again after the release combo. */
  resumeKeys(): void {
    this.keysReleased = false;
  }

  /** The viewer lost focus: lift everything still held. */
  blur(): void {
    this.liftAll();
  }

  /** The session is ending: lift everything and stop any pending flush. */
  dispose(): void {
    this.liftAll();
    this.dropPendingMove();
  }

  private flushMove(): void {
    if (this.cancelFlush) {
      this.cancelFlush();
      this.cancelFlush = null;
    }
    const move = this.pendingMove;
    this.pendingMove = null;
    if (!move || !this.controlGranted) return;
    this.lastMoveAt = this.clock.now();
    this.send({ t: 'mm', x: move.x, y: move.y });
  }

  private dropPendingMove(): void {
    if (this.cancelFlush) this.cancelFlush();
    this.cancelFlush = null;
    this.pendingMove = null;
  }

  private liftKeys(): void {
    for (const [code, key] of this.heldKeys) this.send({ t: 'ku', code, key, m: 0 });
    this.heldKeys.clear();
  }

  private liftAll(): void {
    this.liftKeys();
    for (const b of this.heldButtons) this.send({ t: 'mu', b, x: this.lastX, y: this.lastY });
    this.heldButtons.clear();
  }
}
